// src/layouts/PageLayout.tsx
// ★ Shared page interior shell — wraps page content with Helmet SEO tags,
// Footer, FloatingNav2, and consistent paddingBottom.
//
// The Header is rendered globally in App.tsx, so this component intentionally
// does NOT include it. It handles everything INSIDE the page-content div.

import React from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/common/Footer/Footer';
import FloatingNav from '../components/common/FloatingNavWithChat/FloatingNav';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { CLINIC_CONFIG } from '../config/clinicConfig';

interface PageLayoutProps {
    /** Page title for <title> tag */
    title?: string;
    /** Meta description */
    description?: string;
    /** Meta keywords */
    keywords?: string;
    /** Canonical URL path (will be prepended with clinicUrl) */
    canonicalPath?: string;
    /** JSON-LD schema markup */
    schema?: object[];
    /** Hidden H1 for SEO (common pattern across nearly all pages) */
    hiddenH1?: string;
    /** Open Graph image path (relative or absolute URL) */
    ogImage?: string;
    /** Open Graph type (default: "website") */
    ogType?: string;
    /** Skip Footer rendering (for pages that have custom footers) */
    noFooter?: boolean;
    /** Skip FloatingNav rendering */
    noFloatingNav?: boolean;
    /** Additional className for the wrapper div (e.g. seasonal themes) */
    wrapperClassName?: string;
    /** Page content */
    children: React.ReactNode;
}

const DEFAULT_OG_IMAGE = '/images/logo.webp';

const PageLayout: React.FC<PageLayoutProps> = ({
    title,
    description,
    keywords,
    canonicalPath,
    schema,
    hiddenH1,
    ogImage,
    ogType = 'website',
    noFooter = false,
    noFloatingNav = false,
    wrapperClassName,
    children,
}) => {
    const fullUrl = canonicalPath ? CLINIC_CONFIG.clinicUrl + canonicalPath : CLINIC_CONFIG.clinicUrl;
    const resolvedOgImage = ogImage
        ? (ogImage.startsWith('http') ? ogImage : CLINIC_CONFIG.clinicUrl + ogImage)
        : CLINIC_CONFIG.clinicUrl + DEFAULT_OG_IMAGE;

    return (
        <>
            <Helmet>
                {title && <title>{title}</title>}
                {description && <meta name="description" content={description} />}
                {keywords && <meta name="keywords" content={keywords} />}
                {canonicalPath && (
                    <link rel="canonical" href={fullUrl} />
                )}

                {/* Open Graph */}
                {title && <meta property="og:title" content={title} />}
                {description && <meta property="og:description" content={description} />}
                <meta property="og:type" content={ogType} />
                <meta property="og:url" content={fullUrl} />
                <meta property="og:image" content={resolvedOgImage} />
                <meta property="og:site_name" content={CLINIC_CONFIG.clinicName} />
                <meta property="og:locale" content="en_US" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                {title && <meta name="twitter:title" content={title} />}
                {description && <meta name="twitter:description" content={description} />}
                <meta name="twitter:image" content={resolvedOgImage} />

                {schema && (
                    <script type="application/ld+json">{JSON.stringify(schema)}</script>
                )}
            </Helmet>

            <div style={{ paddingBottom: 130 }} className={wrapperClassName}>
                {hiddenH1 && <h1 className="visually-hidden">{hiddenH1}</h1>}
                <ErrorBoundary>
                    {children}
                </ErrorBoundary>
                {!noFooter && <Footer />}
            </div>
            {!noFloatingNav && <FloatingNav />}
        </>
    );
};

export default PageLayout;
